import React, { useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import TagInput from '../shared/TailwindComponents/TagInput';
import Button from '../shared/TailwindComponents/Button';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  addClauseRule,
  updateClauseRule,
} from '../../store/actions/Review/ReviewActions';
import { ClauseRule } from '../../types/review';

interface ClauseRuleFormPanelProps {
  reviewUuid: string;
  rule?: ClauseRule;
  onClose: () => void;
}

const ClauseRuleFormPanel = forwardRef<any, ClauseRuleFormPanelProps>(({ reviewUuid, rule, onClose }, ref) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isEditing = !!rule;

  const [form, setForm] = useState({
    clause_type: rule?.clause_type || '',
    display_name: rule?.display_name || '',
    description: rule?.description || '',
    starting_position: rule?.starting_position || '',
    acceptable_variations: rule?.acceptable_variations || [],
    red_lines: rule?.red_lines || [],
    fallback_position: rule?.fallback_position || '',
    risk_weight: rule?.risk_weight || 5,
    tags: rule?.tags || [],
    notes: rule?.notes || '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const updateField = <K extends keyof typeof form>(key: K, value: typeof form[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const riskColor = form.risk_weight >= 8 ? 'text-red-600' : form.risk_weight >= 4 ? 'text-amber-600' : 'text-emerald-600';
  const riskTrackBg = form.risk_weight >= 8 ? 'accent-red-600' : form.risk_weight >= 4 ? 'accent-amber-500' : 'accent-emerald-500';

  const handleSave = useCallback(async () => {
    if (!form.clause_type.trim()) return;
    setIsSaving(true);
    try {
      if (isEditing && rule) {
        await dispatch(updateClauseRule(reviewUuid, rule.uuid, form) as any);
      } else {
        await dispatch(addClauseRule(reviewUuid, form) as any);
      }
      onClose();
    } catch (err: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: err?.message || t('Failed to save clause rule'),
        duration: 3000,
      }) as any);
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, reviewUuid, form, isEditing, rule, onClose, t]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  return (
    <div className="p-6 space-y-6">
      {/* Identity Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {t('Identity')}
        </h4>
        <Input
          type="text"
          id="panel-clause-type"
          label={t('Clause Type')}
          placeholder={t('e.g. non_compete, indemnification')}
          value={form.clause_type}
          onChange={(e) => updateField('clause_type', e.target.value)}
          required
        />
        <Input
          type="text"
          id="panel-display-name"
          label={t('Display Name')}
          placeholder={t('e.g. Non-Compete Clause')}
          value={form.display_name}
          onChange={(e) => updateField('display_name', e.target.value)}
        />
        <Textarea
          id="panel-description"
          label={t('Description')}
          placeholder={t('What does this clause rule check?')}
          value={form.description}
          onChange={(e) => updateField('description', e.target.value)}
          rows={2}
        />
      </div>

      {/* Firm Position Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {t('Firm Position')}
        </h4>
        <Textarea
          id="panel-starting-position"
          label={t('Starting Position')}
          placeholder={t("Your firm's preferred starting position for this clause...")}
          value={form.starting_position}
          onChange={(e) => updateField('starting_position', e.target.value)}
          rows={3}
        />
        <TagInput
          id="panel-acceptable-variations"
          label={t('Acceptable Variations')}
          placeholder={t('Press Enter to add a variation')}
          tags={form.acceptable_variations}
          onChange={(v) => updateField('acceptable_variations', v)}
        />
        <TagInput
          id="panel-red-lines"
          label={t('Red Lines (deal-breakers)')}
          placeholder={t('Press Enter to add a red line')}
          tags={form.red_lines}
          onChange={(v) => updateField('red_lines', v)}
        />
        <Textarea
          id="panel-fallback-position"
          label={t('Fallback Position')}
          placeholder={t('Position to fall back to if starting position is rejected...')}
          value={form.fallback_position}
          onChange={(e) => updateField('fallback_position', e.target.value)}
          rows={2}
        />
      </div>

      {/* Classification Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {t('Classification')}
        </h4>
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
            {t('Risk Weight')} <span className={clsx('font-bold', riskColor)}>({form.risk_weight})</span>
          </label>
          <input
            type="range"
            min={1}
            max={10}
            value={form.risk_weight}
            onChange={(e) => updateField('risk_weight', Number(e.target.value))}
            className={clsx('w-full', riskTrackBg)}
          />
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>{t('Low')}</span>
            <span>{t('Medium')}</span>
            <span>{t('High')}</span>
          </div>
        </div>
        <TagInput
          id="panel-tags"
          label={t('Tags')}
          placeholder={t('Press Enter to add')}
          tags={form.tags}
          onChange={(v) => updateField('tags', v)}
        />
      </div>

      {/* Notes Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {t('Notes')}
        </h4>
        <Textarea
          id="panel-notes"
          label={t('Notes')}
          placeholder={t('Any additional notes...')}
          value={form.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          rows={3}
        />
      </div>

    </div>
  );
});

export default ClauseRuleFormPanel;
